import { $, $$, setBusy } from './lib.js';

/** Dropdown menus: [data-menu] > [data-menu-trigger] + [data-menu-panel]. */
function initMenus() {
  const closeAll = (except) => {
    $$('[data-menu-panel]').forEach((panel) => {
      if (panel === except) return;
      panel.hidden = true;
      panel.closest('[data-menu]')?.querySelector('[data-menu-trigger]')?.setAttribute('aria-expanded', 'false');
    });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-menu-trigger]');
    if (trigger) {
      event.preventDefault();
      const panel = trigger.closest('[data-menu]').querySelector('[data-menu-panel]');
      const open = panel.hidden;
      closeAll(panel);
      panel.hidden = !open;
      trigger.setAttribute('aria-expanded', String(open));
      if (open) panel.querySelector('a, button')?.focus();
      return;
    }
    if (!event.target.closest('[data-menu-panel]')) closeAll();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openPanel = $$('[data-menu-panel]').find((p) => !p.hidden);
    if (openPanel) {
      event.preventDefault();
      closeAll();
      openPanel.closest('[data-menu]').querySelector('[data-menu-trigger]')?.focus();
    }
  });
}

/** Full-height page menu: [data-site-nav] > [data-site-nav-toggle] + [data-site-nav-layer]. */
function initSiteNav() {
  $$('[data-site-nav]').forEach((nav) => {
    const toggle = nav.querySelector('[data-site-nav-toggle]');
    const layer = nav.querySelector('[data-site-nav-layer]');
    const panel = nav.querySelector('[data-site-nav-panel]');
    if (!toggle || !layer || !panel) return;

    const iconOpen = toggle.querySelector('[data-site-nav-icon="open"]');
    const iconClose = toggle.querySelector('[data-site-nav-icon="close"]');

    const focusables = () => [toggle, ...panel.querySelectorAll('a[href], button:not([disabled])')];

    const set = (open) => {
      if (open) {
        document.documentElement.style.setProperty('--site-nav-bottom', `${nav.getBoundingClientRect().bottom}px`);
      }
      layer.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      if (iconOpen) iconOpen.hidden = open;
      if (iconClose) iconClose.hidden = !open;
      document.documentElement.classList.toggle('site-nav-open', open);
      if (open) panel.focus();
    };

    toggle.addEventListener('click', () => set(layer.hidden));

    document.addEventListener('keydown', (event) => {
      if (layer.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        set(false);
        toggle.focus();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    window.addEventListener('resize', () => {
      if (getComputedStyle(toggle).display === 'none') set(false);
    });
  });
}

function initSidebar() {
  const sidebar = $('[data-sidebar]');
  const backdrop = $('[data-sidebar-backdrop]');
  const openButton = $('[data-sidebar-open]');
  if (!sidebar) return;
  const mobile = () => window.matchMedia('(max-width: 1023px)').matches;
  const set = (open) => {
    sidebar.classList.toggle('hidden', !open);
    sidebar.classList.toggle('flex', open);
    backdrop?.classList.toggle('hidden', !open);
    document.documentElement.classList.toggle('sidebar-open', open && mobile());
    if (open) sidebar.querySelector('[data-sidebar-close]')?.focus();
  };
  const close = (restore) => {
    set(false);
    if (restore) openButton?.focus();
  };
  openButton?.addEventListener('click', () => set(true));
  $('[data-sidebar-close]')?.addEventListener('click', () => close(true));
  backdrop?.addEventListener('click', () => close(true));
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented || !mobile() || sidebar.classList.contains('hidden')) return;
    event.preventDefault();
    close(true);
  });
  window.addEventListener('resize', () => {
    if (!mobile()) set(false);
  });
}

function initForms() {
  // Confirm destructive or expensive actions.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const message = form.dataset.confirm;
      if (message && !window.confirm(message)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true,
  );

  // Prevent double submits and show progress on ordinary forms.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (event.defaultPrevented || form.dataset.async !== undefined || form.hasAttribute('data-generate-form')) return;
    const button = form.querySelector('button[type="submit"], button:not([type])');
    if (form.dataset.longTask) {
      setBusy(button, true, form.dataset.longTask);
    } else if (button?.dataset.loadingText) {
      setBusy(button, true, button.dataset.loadingText);
    } else if (button) {
      setTimeout(() => (button.disabled = true), 0);
    }
  });

  $$('[data-autosubmit]').forEach((el) => el.addEventListener('change', () => el.form?.requestSubmit()));
}

function initMisc() {
  document.addEventListener('click', (event) => {
    const dismiss = event.target.closest('[data-dismiss]');
    if (dismiss) dismiss.closest('[data-dismissible]')?.remove();

    if (event.target.closest('[data-history-back]')) history.back();

    // Clickable table rows, without hijacking inner links/buttons or text selection.
    const row = event.target.closest('tr[data-href]');
    if (row && !event.target.closest('a, button, input, select, form') && !window.getSelection()?.toString()) {
      window.location.href = row.dataset.href;
    }
  });
}

export function initUi() {
  initMenus();
  initSiteNav();
  initSidebar();
  initForms();
  initMisc();
}
